import { isApolloError } from '@apollo/client/errors';
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  ErrorEvent,
  EventHint,
  SeverityLevel,
} from '@sentry/nextjs';

type Breadcrumb = {
  level: SeverityLevel;
  message: string;
  category?: string;
};

export const captureBreadcrumb = (breadcrumb: Breadcrumb) => {
  addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level,
  });
};

export const captureError = captureException;

export const captureErrorMessage = captureMessage;

// An ApolloError's stack only has Apollo frames, so Sentry groups every failed
// operation into one issue. The Apollo error link records which operation each
// GraphQL/network error came from, and tagApolloError puts it on the event.
const operationNames = new WeakMap<object, string>();

export const rememberApolloOperation = (
  error: object,
  operationName: string,
) => {
  operationNames.set(error, operationName);
};

export const tagApolloError = (event: ErrorEvent, hint: EventHint) => {
  const error = hint.originalException;
  if (!(error instanceof Error) || !isApolloError(error)) return event;

  const source = [error.networkError, ...error.graphQLErrors].find(
    (err) => err && operationNames.has(err),
  );
  const operationName = source ? operationNames.get(source) : undefined;
  if (!operationName) return event;

  return {
    ...event,
    tags: { ...event.tags, 'graphql.operation': operationName },
    fingerprint: ['ApolloError', operationName],
  };
};

// The browser's own wording when fetch gets no response at all: the connection
// dropped, or iOS suspended the page mid-request. cross-fetch's polyfill says
// "Network request failed"; native fetch says one of the others.
const fetchFailureMessages = new Set([
  'Network request failed',
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
]);

// An ApolloError for a request that never reached the API, so there is nothing
// for us to fix. A failed request that got a response (a 4xx or 5xx) has a
// statusCode, and one that reached a resolver has GraphQL errors.
const isDroppedConnection = (error: unknown) =>
  error instanceof Error &&
  isApolloError(error) &&
  !error.graphQLErrors.length &&
  !!error.networkError &&
  !('statusCode' in error.networkError) &&
  fetchFailureMessages.has(error.networkError.message);

// Our code always runs from `/_next/static` chunks, which Sentry rewrites to
// `app:///_next/...`. A frame whose file is the page itself (`app:///artists/x`)
// comes from a script injected into the document, such as the one Chrome and
// the Google app on iOS add, which overflows the stack walking the DOM.
const isDocumentFrame = ({ filename }: { filename?: string }) =>
  !!filename &&
  filename.startsWith('app:///') &&
  !filename.startsWith('app:///_next/') &&
  !/\.m?js(\?|$)/.test(filename);

// WebKit adds `[native code]` frames for built-ins such as Promise. A stack of
// only those means the script was evaluated with no URL, which ours never is.
const isNativeFrame = ({ filename }: { filename?: string }) =>
  filename === '[native code]';

const isFromInjectedScript = (event: ErrorEvent) => {
  const frames = event.exception?.values?.flatMap(
    (value) => value.stacktrace?.frames ?? [],
  );
  return (
    !!frames?.length &&
    frames.every((frame) => isDocumentFrame(frame) || isNativeFrame(frame))
  );
};

// When a fake `unhandledrejection` event carries a `reason`, or a `detail` with
// one, Sentry unwraps it and beforeSend only sees that reason (often `{}`, seen
// from Safari on macOS). Remember those reasons so beforeSend can drop them.
const syntheticRejectionReasons = new WeakSet<object>();

export const rememberSyntheticRejection = (event: Event) => {
  if (event.isTrusted) return;

  const { detail, reason } = event as {
    detail?: { reason?: unknown } | null;
    reason?: unknown;
  };
  const rejected = 'reason' in event ? reason : detail?.reason;
  if (rejected && typeof rejected === 'object') {
    syntheticRejectionReasons.add(rejected);
  }
};

// A script (an extension, a third-party tag) can dispatch a fake DOM event such
// as `new CustomEvent('unhandledrejection')`, which Sentry reports with no
// stack. Real rejections are trusted and Sentry reports their reason, not the
// event, so an untrusted event in originalException is never ours.
export const beforeSend = (event: ErrorEvent, hint: EventHint) => {
  const error = hint.originalException;
  if (error instanceof Event && !error.isTrusted) return null;
  if (syntheticRejectionReasons.has(error as object)) return null;
  if (isFromInjectedScript(event)) return null;
  if (isDroppedConnection(error)) return null;

  return tagApolloError(event, hint);
};

// Browser extensions inject scripts into every page, such as MetaMask's
// `scripts/inpage.js`, which throws "Failed to connect to MetaMask" while
// restoring a wallet session on its own. The Next.js SDK rewrites every frame's
// origin to `app://`, which hides the extension from Sentry's server-side
// filter, but `denyUrls` runs before that rewrite and still sees the real URL.
// Legacy Safari App Extensions, such as PayPal Honey, run their scripts from a
// `file://` URL inside the app bundle; the site itself is never served from one.
export const browserExtensionUrls = [
  /^(chrome|moz|safari-web)-extension:\/\//,
  /^file:\/\//,
];

// GTM queues dataLayer events until gtm.js loads, then sends one GA4 hit per
// event at once, so page loads show a burst of identical /g/collect requests
// that Sentry flags as an N+1 API call. Analytics beacons say nothing about
// our app's performance, so don't create spans for them.
const analyticsHosts = new Set([
  'gtm.svc.avantarte.com',
  'api-eu.mixpanel.com',
  'www.google.com',
  'ad.doubleclick.net',
]);

export const shouldCreateSpanForRequest = (url: string) => {
  try {
    return !analyticsHosts.has(new URL(url, 'https://avantarte.com').hostname);
  } catch {
    return true;
  }
};
